// ReviewerDashboard.jsx — V2
import React, { useState, useEffect, useRef } from 'react';
import {
  Card, Table, Button, Modal, Input, Tag, Space, message,
  Descriptions, Typography, Row, Col, DatePicker,
  Select, Rate, Tabs, Timeline, Spin, Divider, Alert, Drawer, Tooltip,
} from 'antd';
import {
  CheckCircleOutlined, CloseCircleOutlined, EyeOutlined,
  FilterOutlined, CopyOutlined, FolderOpenOutlined,
  CheckOutlined, HistoryOutlined, StarFilled,
  DownOutlined, UpOutlined, EditOutlined, MessageOutlined,
  ReloadOutlined, FileTextOutlined, LinkOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import moment from 'moment';
import reviewApiV2 from './reviewApiV2';
import webSocketService from '../../components/WebSocketService';
import ProjectChat from '../../components/pages/ProjectChat';

const { TextArea }    = Input;
const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option }      = Select;

const BASE = 'https://api.eilandigital.com/eilanApplication-0.0.1-SNAPSHOT';

// =========================================================
// EDIT HISTORY BREAKDOWN
// =========================================================
const EditHistoryBreakdown = ({ editHistory }) => {
  const [expanded, setExpanded] = useState(false);

  if (!editHistory || editHistory.length === 0) return null;

  const fmt = (dateStr) => {
    if (!dateStr) return '';
    try { return moment(dateStr).format('DD MMM YYYY, hh:mm A'); }
    catch { return dateStr; }
  };

  const actionLabel = (a) =>
    (a || '').toLowerCase() === 'approved' ? 'Approved' : 'Retake';

  const ratingFields = [
    { key: 'quality_rating',  label: 'Quality',  color: '#1890ff' },
    { key: 'deadline_rating', label: 'Deadline', color: '#52c41a' },
    { key: 'brief_rating',    label: 'Brief',    color: '#fa8c16' },
    { key: 'retake_rating',   label: 'Retake',   color: '#722ed1' },
  ];

  return (
    <div style={{ marginBottom: 8, border: '1px dashed #d9d9d9', borderRadius: 6, overflow: 'hidden' }}>
      <div
        onClick={() => setExpanded(v => !v)}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '6px 12px', background: '#fafafa', cursor: 'pointer', userSelect: 'none',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <EditOutlined style={{ color: '#fa8c16', fontSize: 12 }} />
          <Text style={{ fontSize: 12, color: '#fa8c16', fontWeight: 600 }}>
            Edited {editHistory.length} {editHistory.length === 1 ? 'time' : 'times'}
          </Text>
          <Text style={{ fontSize: 11, color: '#8c8c8c' }}>
            Last edited {fmt(editHistory[0]?.edited_at)}
          </Text>
        </div>
        {expanded
          ? <UpOutlined   style={{ fontSize: 10, color: '#8c8c8c' }} />
          : <DownOutlined style={{ fontSize: 10, color: '#8c8c8c' }} />}
      </div>

      {expanded && (
        <div style={{ padding: '0 12px 12px 12px' }}>
          {editHistory.map((edit, idx) => {
            const actionChanged   = edit.old_action !== edit.new_action;
            const commentsChanged = edit.old_comments !== edit.new_comments;
            const ratingChanged   = ratingFields.some(r => edit[`old_${r.key}`] !== edit[`new_${r.key}`]);
            const internalChanged = edit.old_internal_rating !== edit.new_internal_rating;

            return (
              <div key={idx} style={{ marginTop: 10, paddingTop: 10, borderTop: idx > 0 ? '1px solid #f0f0f0' : 'none' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                  <Text style={{ fontSize: 11, color: '#8c8c8c', fontWeight: 600 }}>Edit #{editHistory.length - idx}</Text>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    {edit.edited_by_name && (
                      <Text style={{ fontSize: 11, color: '#8c8c8c' }}>by <strong>{edit.edited_by_name}</strong></Text>
                    )}
                    <Text style={{ fontSize: 11, color: '#8c8c8c' }}>{fmt(edit.edited_at)}</Text>
                  </div>
                </div>

                {actionChanged && (
                  <div style={{
                    display: 'grid', gridTemplateColumns: '1fr auto 1fr',
                    alignItems: 'center', gap: 8, marginBottom: 8,
                    background: '#fff7e6', border: '1px solid #ffd591',
                    borderRadius: 6, padding: '8px 12px',
                  }}>
                    <div>
                      <Text style={{ fontSize: 10, color: '#8c8c8c', display: 'block', marginBottom: 2 }}>BEFORE</Text>
                      <Tag color={edit.old_action === 'approved' ? 'success' : 'error'} style={{ margin: 0 }}>
                        {actionLabel(edit.old_action)}
                      </Tag>
                    </div>
                    <Text style={{ fontSize: 16, color: '#d46b08' }}>→</Text>
                    <div>
                      <Text style={{ fontSize: 10, color: '#8c8c8c', display: 'block', marginBottom: 2 }}>AFTER</Text>
                      <Tag color={edit.new_action === 'approved' ? 'success' : 'error'} style={{ margin: 0 }}>
                        {actionLabel(edit.new_action)}
                      </Tag>
                    </div>
                  </div>
                )}

                {(ratingChanged || internalChanged) && (
                  <div style={{ background: '#fafafa', border: '1px solid #f0f0f0', borderRadius: 6, padding: '8px 12px', marginBottom: 8 }}>
                    <Text style={{ fontSize: 10, color: '#8c8c8c', fontWeight: 700, display: 'block', marginBottom: 6 }}>RATING CHANGES</Text>
                    {internalChanged && (
                      <div style={{ display: 'grid', gridTemplateColumns: '80px 1fr auto 1fr', alignItems: 'center', gap: 8, marginBottom: 6, paddingBottom: 6, borderBottom: '1px dashed #e8e8e8' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <StarFilled style={{ color: '#FFD700', fontSize: 11 }} />
                          <Text style={{ fontSize: 11, color: '#8c8c8c' }}>Internal</Text>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <Rate disabled allowHalf value={edit.old_internal_rating || 0} style={{ fontSize: 11, color: '#d9d9d9' }} />
                          <Text style={{ fontSize: 11, color: '#8c8c8c' }}>{edit.old_internal_rating ? `${edit.old_internal_rating}/5` : '-'}</Text>
                        </div>
                        <Text style={{ fontSize: 14, color: '#d46b08' }}>→</Text>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <Rate disabled allowHalf value={edit.new_internal_rating || 0} style={{ fontSize: 11, color: '#FFD700' }} />
                          <Text style={{ fontSize: 11, color: '#262626', fontWeight: 600 }}>{edit.new_internal_rating ? `${edit.new_internal_rating}/5` : '-'}</Text>
                        </div>
                      </div>
                    )}
                    {ratingFields.map(r => {
                      const oldVal = edit[`old_${r.key}`] || 0;
                      const newVal = edit[`new_${r.key}`] || 0;
                      if (oldVal === newVal) return null;
                      return (
                        <div key={r.key} style={{ display: 'grid', gridTemplateColumns: '80px 1fr auto 1fr', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                          <Text style={{ fontSize: 11, color: r.color, fontWeight: 600 }}>{r.label}</Text>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            <Rate disabled value={oldVal} style={{ fontSize: 11, color: '#d9d9d9' }} />
                            <Text style={{ fontSize: 11, color: '#8c8c8c' }}>{oldVal > 0 ? `${oldVal}/5` : '-'}</Text>
                          </div>
                          <Text style={{ fontSize: 14, color: '#d46b08' }}>→</Text>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            <Rate disabled value={newVal} style={{ fontSize: 11, color: '#FFD700' }} />
                            <Text style={{ fontSize: 11, color: '#262626', fontWeight: 600 }}>{newVal > 0 ? `${newVal}/5` : '-'}</Text>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {commentsChanged && (
                  <div style={{ marginBottom: 4 }}>
                    <Text style={{ fontSize: 10, color: '#8c8c8c', fontWeight: 700, display: 'block', marginBottom: 6 }}>COMMENT CHANGES</Text>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                      <div style={{ background: '#fff1f0', border: '1px solid #ffa39e', borderRadius: 4, padding: '6px 10px' }}>
                        <Text style={{ fontSize: 10, color: '#cf1322', fontWeight: 700, display: 'block', marginBottom: 4 }}>BEFORE</Text>
                        <Text style={{ fontSize: 12, color: '#374151', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{edit.old_comments || '-'}</Text>
                      </div>
                      <div style={{ background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: 4, padding: '6px 10px' }}>
                        <Text style={{ fontSize: 10, color: '#389e0d', fontWeight: 700, display: 'block', marginBottom: 4 }}>AFTER</Text>
                        <Text style={{ fontSize: 12, color: '#374151', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{edit.new_comments || '-'}</Text>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// =========================================================
// EDIT REVIEW MODAL
// =========================================================
const EditReviewModal = ({ open, onClose, reviewAction, submissionId, onSuccess, currentUserId }) => {
  const [newAction,         setNewAction]         = useState('');
  const [newComments,       setNewComments]       = useState('');
  const [newQualityRating,  setNewQualityRating]  = useState(0);
  const [newDeadlineRating, setNewDeadlineRating] = useState(0);
  const [newBriefRating,    setNewBriefRating]    = useState(0);
  const [newRetakeRating,   setNewRetakeRating]   = useState(0);
  const [submitting,        setSubmitting]        = useState(false);

  const allRatingsFilled =
    newQualityRating > 0 && newDeadlineRating > 0 &&
    newBriefRating > 0 && newRetakeRating > 0;

  const newInternalRating = allRatingsFilled
    ? ((newQualityRating + newDeadlineRating + newBriefRating + newRetakeRating) / 4).toFixed(1)
    : 0;

  useEffect(() => {
    if (open && reviewAction) {
      setNewAction(reviewAction.action         || '');
      setNewComments(reviewAction.comments     || '');
      setNewQualityRating(reviewAction.quality_rating   || 0);
      setNewDeadlineRating(reviewAction.deadline_rating || 0);
      setNewBriefRating(reviewAction.brief_rating       || 0);
      setNewRetakeRating(reviewAction.retake_rating     || 0);
    }
  }, [open, reviewAction]);

  const handleSubmit = async () => {
    if (newAction === 'approved') {
      if (newQualityRating  === 0) { message.warning('Please provide Quality Rating');  return; }
      if (newDeadlineRating === 0) { message.warning('Please provide Deadline Rating'); return; }
      if (newBriefRating    === 0) { message.warning('Please provide Brief Rating');    return; }
      if (newRetakeRating   === 0) { message.warning('Please provide Retake Rating');   return; }
      if (!newComments.trim())     { message.warning('Remarks are required');           return; }
    }
    if (newAction === 'rejected') {
      if (!newComments.trim()) { message.warning('Please provide retake reason'); return; }
    }

    setSubmitting(true);
    try {
      const avgRating = newAction === 'approved' && allRatingsFilled
        ? parseFloat(((newQualityRating + newDeadlineRating + newBriefRating + newRetakeRating) / 4).toFixed(1))
        : null;

      const res = await reviewApiV2.editReviewAction({
        reviewActionId:    reviewAction.id,
        submissionId,
        editedBy:          currentUserId,
        newAction,
        newComments,
        newQualityRating:  newAction === 'approved' ? newQualityRating  : null,
        newDeadlineRating: newAction === 'approved' ? newDeadlineRating : null,
        newBriefRating:    newAction === 'approved' ? newBriefRating    : null,
        newRetakeRating:   newAction === 'approved' ? newRetakeRating   : null,
        newInternalRating: avgRating,
      });

      if (res.data.success) {
        message.success('Review updated successfully');
        onClose();
        onSuccess();
      } else {
        message.error(res.data.message || 'Failed to update review');
      }
    } catch (err) {
      message.error(err.response?.data?.message || 'Failed to update review');
    } finally {
      setSubmitting(false);
    }
  };

  const RatingRow = ({ label, value, onChange }) => (
    <div>
      <Text strong>{label} <span style={{ color: 'red' }}>*</span></Text>
      <div style={{ marginTop: 6 }}>
        <Rate value={value} onChange={onChange} style={{ fontSize: 22 }} />
        <Text type="secondary" style={{ marginLeft: 10 }}>{value}/5</Text>
      </div>
    </div>
  );

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <EditOutlined style={{ color: '#fa8c16' }} />
          <span>Edit Review</span>
        </div>
      }
      open={open} onCancel={onClose} onOk={handleSubmit}
      confirmLoading={submitting} okText="Save Changes"
      okButtonProps={{ style: { backgroundColor: '#fa8c16', borderColor: '#fa8c16' } }}
      width={600}
    >
      {reviewAction && (
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <Alert type="warning" showIcon message="Editing a review will update the task status if the action changes." />

          <div>
            <Text strong>Review Decision <span style={{ color: 'red' }}>*</span></Text>
            <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
              <Button
                type={newAction === 'approved' ? 'primary' : 'default'}
                icon={<CheckCircleOutlined />}
                onClick={() => setNewAction('approved')}
                style={newAction === 'approved' ? { backgroundColor: '#52c41a', borderColor: '#52c41a' } : {}}
              >Approve</Button>
              <Button
                type={newAction === 'rejected' ? 'primary' : 'default'}
                danger={newAction === 'rejected'}
                icon={<CloseCircleOutlined />}
                onClick={() => setNewAction('rejected')}
              >Retake</Button>
            </div>
          </div>

          {newAction === 'approved' && (
            <>
              <RatingRow label="Quality Rating"  value={newQualityRating}  onChange={setNewQualityRating} />
              <RatingRow label="Deadline Rating" value={newDeadlineRating} onChange={setNewDeadlineRating} />
              <RatingRow label="Brief Rating"    value={newBriefRating}    onChange={setNewBriefRating} />
              <RatingRow label="Retake Rating"   value={newRetakeRating}   onChange={setNewRetakeRating} />
              {allRatingsFilled && (
                <div style={{ backgroundColor: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: 4, padding: 12 }}>
                  <Text strong>Internal Rating (Auto-calculated)</Text>
                  <div style={{ marginTop: 8 }}>
                    <Rate disabled allowHalf value={parseFloat(newInternalRating)} style={{ fontSize: 22, color: '#FFD700' }} />
                    <Text type="secondary" style={{ marginLeft: 10, fontSize: 15 }}>{newInternalRating}/5</Text>
                  </div>
                </div>
              )}
            </>
          )}

          <div>
            <Text strong>
              {newAction === 'approved' ? 'Remarks' : 'Retake Reason'}
              <span style={{ color: 'red', marginLeft: 4 }}>*</span>
            </Text>
            <TextArea
              rows={4} value={newComments}
              onChange={(e) => setNewComments(e.target.value)}
              placeholder={newAction === 'approved' ? 'Enter remarks (required)' : 'Explain what needs to be fixed (required)'}
              style={{ marginTop: 8 }}
            />
          </div>
        </Space>
      )}
    </Modal>
  );
};

// =========================================================
// SUBMISSION HISTORY TIMELINE
// =========================================================
const SubmissionHistory = ({ taskId, version, ownerId, currentUserId, onReviewEdited }) => {
  const [loading,          setLoading]          = useState(false);
  const [history,          setHistory]          = useState([]);
  const [showAll,          setShowAll]          = useState(false);
  const [editModal,        setEditModal]        = useState(false);
  const [editReviewAction, setEditReviewAction] = useState(null);
  const [editSubmissionId, setEditSubmissionId] = useState(null);
  const [copiedPath,       setCopiedPath]       = useState(null);

  const isNetworkPath = (text) =>
    text && /^\\\\[\w\d.-]+(\\[\w\s.-]+)*/.test(text.trim());

  const isHttpLink = (text) =>
    text && /^https?:\/\/[^\s]+$/i.test(text.trim());

  const handleCopyPath = (path) => {
    navigator.clipboard.writeText(path).then(() => {
      setCopiedPath(path);
      setTimeout(() => setCopiedPath(null), 2000);
    }).catch(() => message.error('Failed to copy path'));
  };

  const renderTextWithPaths = (text) => {
    if (!text) return null;
    return text.split('\n').map((line, index) => {
      const t = line.trim();
      if (isNetworkPath(t)) {
        const isCopied = copiedPath === t;
        return (
          <div key={index} style={{
            display: 'flex', alignItems: 'center', gap: '8px',
            padding: '8px 12px', background: '#f0f5ff',
            border: '1px solid #d6e4ff', borderRadius: '4px',
            marginBottom: '4px', fontFamily: 'monospace', fontSize: '12px',
          }}>
            <FolderOpenOutlined style={{ color: '#1890ff', fontSize: '16px' }} />
            <span style={{ flex: 1, wordBreak: 'break-all', color: '#1890ff' }}>{t}</span>
            <Button type="text" size="small"
              icon={isCopied ? <CheckOutlined /> : <CopyOutlined />}
              onClick={() => handleCopyPath(t)}
              style={{ color: isCopied ? '#52c41a' : '#1890ff' }}
            />
          </div>
        );
      }
      if (isHttpLink(t)) {
        return (
          <div key={index} style={{
            display: 'flex', alignItems: 'center', gap: '8px',
            padding: '8px 12px', background: '#f6ffed',
            border: '1px solid #b7eb8f', borderRadius: '4px', marginBottom: '4px',
          }}>
            <span style={{ fontSize: '16px' }}>🔗</span>
            <a href={t} target="_blank" rel="noopener noreferrer"
              style={{ flex: 1, wordBreak: 'break-all', color: '#52c41a', fontWeight: 500 }}>
              {t}
            </a>
          </div>
        );
      }
      return (
        <div key={index} style={{ marginBottom: '4px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
          {line}
        </div>
      );
    });
  };

  useEffect(() => {
    if (taskId && version && ownerId) fetchHistory();
  }, [taskId, version, ownerId]);

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const subRes = await axios.get(`${BASE}/v2/review/getOwnerSubmissionHistory`, {
        params: { taskId, version, ownerId },
      });
      const submissions = subRes.data?.data || [];
      const enriched = await Promise.all(
        submissions.map(async (sub) => {
          try {
            const revRes = await axios.get(`${BASE}/v2/review/getSubmissionReviewHistory`, {
              params: { submissionId: sub.submission_id },
            });
            const reviewActions = revRes.data?.data || [];
            const actionsWithEdits = await Promise.all(
              reviewActions.map(async (action) => {
                try {
                  const editRes = await axios.get(`${BASE}/v2/review/getReviewEditHistory`, {
                    params: { reviewActionId: action.id },
                  });
                  return { ...action, editHistory: editRes.data?.data || [] };
                } catch {
                  return { ...action, editHistory: [] };
                }
              })
            );
            return { ...sub, reviewActions: actionsWithEdits };
          } catch {
            return { ...sub, reviewActions: [] };
          }
        })
      );
      enriched.sort((a, b) => new Date(b.submitted_at) - new Date(a.submitted_at));
      setHistory(enriched);
    } catch (err) {
      console.error('Error fetching submission history:', err);
      message.error('Failed to load submission history');
    } finally {
      setLoading(false);
    }
  };

  const handleEditSuccess = () => {
    fetchHistory();
    if (onReviewEdited) onReviewEdited();
  };

  const fmt = (dateStr) => {
    if (!dateStr) return '';
    try { return moment(dateStr).format('DD MMM YYYY, hh:mm A'); }
    catch { return dateStr; }
  };

  const getVersionLabel = (internalVersion) =>
    `${String(version).padStart(2, '0')}-${String(internalVersion || 0).padStart(2, '0')}`;

  const visibleHistory = showAll ? history : history.slice(0, 1);
  const hasMore        = history.length > 1;

  const renderSubmission = (sub, idx) => {
    const isApproved = sub.status === 'approved';
    const isRejected = sub.status === 'rejected';
    const dotColor   = isApproved ? '#52c41a' : isRejected ? '#ff4d4f' : '#1890ff';
    const dotIcon    = isApproved
      ? <CheckCircleOutlined style={{ color: '#52c41a' }} />
      : isRejected
      ? <CloseCircleOutlined style={{ color: '#ff4d4f' }} />
      : undefined;
    const isLatest = idx === 0;

    return (
      <Timeline.Item key={sub.submission_id} color={dotColor} dot={dotIcon}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
          <Tag color="blue" style={{ margin: 0, fontWeight: 600 }}>v{getVersionLabel(sub.internal_version_snapshot)}</Tag>
          <Tag color={isApproved ? 'success' : isRejected ? 'error' : 'processing'} style={{ margin: 0 }}>
            {isApproved ? 'Approved' : isRejected ? 'Retake' : sub.status === 'under_review' ? 'Under Review' : 'Pending'}
          </Tag>
          {isLatest && <Tag color="gold" style={{ margin: 0 }}>Latest</Tag>}
        </div>

        {sub.submission_notes && (
          <div style={{ background: '#e6f7ff', border: '1px solid #91d5ff', borderRadius: 6, padding: '8px 12px', marginBottom: 8 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
              <Text style={{ fontSize: 11, color: '#096dd9', fontWeight: 700 }}>SUBMISSION NOTES</Text>
              <Text style={{ fontSize: 11, color: '#8c8c8c' }}>{fmt(sub.submitted_at)}</Text>
            </div>
            <div style={{ fontSize: 13, color: '#374151', lineHeight: 1.6 }}>
              {renderTextWithPaths(sub.submission_notes)}
            </div>
          </div>
        )}

        {(sub.reviewActions || []).map((action, aIdx) => {
          const isActionApproved = (action.action || '').toLowerCase() === 'approved';
          const canEdit          = isLatest && action.reviewer_id === currentUserId;
          const hasEdits         = (action.editHistory || []).length > 0;

          return (
            <div key={aIdx}>
              {isActionApproved && (action.internal_rating > 0 || action.quality_rating > 0) && (
                <div style={{ background: '#fafafa', border: '1px solid #f0f0f0', borderRadius: 6, padding: '10px 12px', marginBottom: 8 }}>
                  {action.internal_rating > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, paddingBottom: 8, borderBottom: '1px dashed #e8e8e8' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <StarFilled style={{ color: '#FFD700', fontSize: 13 }} />
                        <Text strong style={{ fontSize: 12 }}>Internal Rating (Avg)</Text>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <Rate disabled allowHalf value={action.internal_rating} style={{ fontSize: 13, color: '#FFD700' }} />
                        <Text strong style={{ fontSize: 13 }}>{Number(action.internal_rating).toFixed(1)}/5</Text>
                      </div>
                    </div>
                  )}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 12px' }}>
                    {[
                      { label: 'Quality',  value: action.quality_rating,  color: '#1890ff' },
                      { label: 'Deadline', value: action.deadline_rating, color: '#52c41a' },
                      { label: 'Brief',    value: action.brief_rating,    color: '#fa8c16' },
                      { label: 'Retake',   value: action.retake_rating,   color: '#722ed1' },
                    ].map(r => (
                      <div key={r.label} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Text style={{ fontSize: 11, color: '#8c8c8c', minWidth: 52 }}>{r.label}</Text>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                          <Rate disabled value={r.value || 0} style={{ fontSize: 11, color: r.value > 0 ? '#FFD700' : '#d9d9d9' }} />
                          <Text style={{ fontSize: 11, color: r.color, fontWeight: 600, minWidth: 22 }}>
                            {r.value > 0 ? `${r.value}/5` : '-'}
                          </Text>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {action.comments && (
                <div style={{
                  background: isActionApproved ? '#f6ffed' : '#fff2e8',
                  border: `1px solid ${isActionApproved ? '#b7eb8f' : '#ffbb96'}`,
                  borderRadius: 6, padding: '8px 12px', marginBottom: 8,
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                    <Text style={{ fontSize: 11, color: isActionApproved ? '#52c41a' : '#fa541c', fontWeight: 700 }}>
                      {isActionApproved ? 'REVIEWER COMMENT' : 'RETAKE REASON'}
                    </Text>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      {action.reviewer_name && (
                        <Text style={{ fontSize: 11, color: '#8c8c8c' }}>by <strong>{action.reviewer_name}</strong></Text>
                      )}
                      <Text style={{ fontSize: 11, color: '#8c8c8c' }}>{fmt(action.reviewed_at)}</Text>
                      {canEdit && (
                        <Button
                          size="small" icon={<EditOutlined />}
                          onClick={() => {
                            setEditReviewAction(action);
                            setEditSubmissionId(sub.submission_id);
                            setEditModal(true);
                          }}
                          style={{ fontSize: 11, height: 22, padding: '0 6px', color: '#fa8c16', borderColor: '#fa8c16' }}
                        >Edit</Button>
                      )}
                    </div>
                  </div>
                  <div style={{ fontSize: 13, color: '#374151', lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {action.comments}
                  </div>
                </div>
              )}

              {hasEdits && <EditHistoryBreakdown editHistory={action.editHistory} />}
            </div>
          );
        })}

        {(sub.reviewActions || []).length === 0 && sub.status !== 'withdrawn' && (
          <Text style={{ fontSize: 12, color: '#bbb', fontStyle: 'italic' }}>Awaiting review</Text>
        )}
      </Timeline.Item>
    );
  };

  return (
    <div style={{ marginTop: 4 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <HistoryOutlined style={{ color: '#1890ff' }} />
        <Text strong style={{ fontSize: 14, color: '#262626' }}>Submission History</Text>
        {history.length > 0 && (
          <Tag style={{ margin: 0 }}>{history.length} {history.length === 1 ? 'submission' : 'submissions'}</Tag>
        )}
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 32 }}><Spin size="large" /></div>
      ) : history.length === 0 ? (
        <div style={{ textAlign: 'center', color: '#bbb', padding: 24 }}>No submission history found</div>
      ) : (
        <div style={{ border: '1px solid #f0f0f0', borderRadius: 8, padding: '16px', background: '#fafafa' }}>
          <Timeline>
            {visibleHistory.map((sub, idx) => renderSubmission(sub, idx))}
          </Timeline>
          {hasMore && (
            <div style={{ textAlign: 'center', marginTop: 4 }}>
              <Button
                type="link"
                icon={showAll ? <UpOutlined /> : <DownOutlined />}
                onClick={() => setShowAll(v => !v)}
                style={{ fontSize: 13, padding: '0 8px' }}
              >
                {showAll ? 'Show Less' : `Show More (${history.length - 1} older ${history.length - 1 === 1 ? 'version' : 'versions'})`}
              </Button>
            </div>
          )}
        </div>
      )}

      <EditReviewModal
        open={editModal}
        onClose={() => { setEditModal(false); setEditReviewAction(null); setEditSubmissionId(null); }}
        reviewAction={editReviewAction}
        submissionId={editSubmissionId}
        currentUserId={currentUserId}
        onSuccess={handleEditSuccess}
      />
    </div>
  );
};

// =========================================================
// BRIEF LINK CELL
// Renders a clean clickable button if brief_link exists,
// or a muted "No brief" placeholder if it does not.
// Handles both http links and network paths gracefully.
// =========================================================
const BriefLinkCell = ({ link }) => {
  if (!link || !link.trim()) {
    return (
      <Text style={{ fontSize: 12, color: '#d9d9d9', fontStyle: 'italic' }}>No brief</Text>
    );
  }

  const trimmed = link.trim();

  // Network path — copy to clipboard
  const isNetworkPath = /^\\\\[\w\d.-]+(\\[\w\s.-]+)*/.test(trimmed);
  if (isNetworkPath) {
    return (
      <Tooltip title={trimmed} placement="topLeft">
        <Button
          size="small"
          icon={<FolderOpenOutlined />}
          onClick={() => {
            navigator.clipboard.writeText(trimmed);
            message.success('Brief path copied');
          }}
          style={{ fontSize: 11, color: '#1890ff', padding: '0 8px' }}
        >
          Copy Path
        </Button>
      </Tooltip>
    );
  }

  // HTTP link — open in new tab
  const href = trimmed.startsWith('http') ? trimmed : `https://${trimmed}`;
  return (
    <Tooltip title={trimmed} placement="topLeft">
      <a href={href} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
        <Button
          size="small"
          // icon={<FileTextOutlined />}
          style={{
            fontSize: 12,
            color: '#042de4ff',
            // borderColor: '#722ed1',
            padding: '0 8px',
          }}
        >
          Brief Link
        </Button>
      </a>
    </Tooltip>
  );
};

// =========================================================
// MAIN COMPONENT
// =========================================================
const ReviewerDashboard = () => {

  const getUserId = () => {
    try {
      const tokenStr = localStorage.getItem('token');
      if (tokenStr) {
        const parsed = JSON.parse(tokenStr);
        const id = parsed?.userId || parsed?.employeeId || parsed?.id || null;
        if (id) return id;
      }
    } catch {}
    try {
      const sessionStr = localStorage.getItem('sessionData');
      if (sessionStr) {
        const session = JSON.parse(sessionStr);
        return session?.userId || null;
      }
    } catch {}
    return null;
  };

  const userId = getUserId();

  const [pendingReviews,  setPendingReviews]  = useState([]);
  const [approvedReviews, setApprovedReviews] = useState([]);
  const [rejectedReviews, setRejectedReviews] = useState([]);
  const [retakeReviews,   setRetakeReviews]   = useState([]);
  const [allReviews,      setAllReviews]      = useState([]);
  const [statistics,      setStatistics]      = useState({});
  const [loading,         setLoading]         = useState(false);
  const [statsLoading,    setStatsLoading]    = useState(false);
  const [reviewModal,     setReviewModal]     = useState(false);
  const [detailModal,     setDetailModal]     = useState(false);
  const [selectedTask,    setSelectedTask]    = useState(null);
  const [reviewAction,    setReviewAction]    = useState(null);
  const [comments,        setComments]        = useState('');
  const [remarks,         setRemarks]         = useState('');
  const [qualityRating,   setQualityRating]   = useState(0);
  const [deadlineRating,  setDeadlineRating]  = useState(0);
  const [briefRating,     setBriefRating]     = useState(0);
  const [retakeRating,    setRetakeRating]    = useState(0);
  const [activeTab,       setActiveTab]       = useState('1');
  const [copiedPath,      setCopiedPath]      = useState(null);

  // Chat drawer
  const [chatDrawerOpen,  setChatDrawerOpen]  = useState(false);
  const [chatProjectData, setChatProjectData] = useState(null);

  const openChatDrawer = (record) => {
    setChatProjectData({ projectId: record.project_id, projectName: record.project_name });
    setChatDrawerOpen(true);
  };
  const closeChatDrawer = () => {
    setChatDrawerOpen(false);
    setTimeout(() => setChatProjectData(null), 300);
  };

  const allRatingsFilled =
    qualityRating > 0 && deadlineRating > 0 && briefRating > 0 && retakeRating > 0;

  const internalRating = allRatingsFilled
    ? ((qualityRating + deadlineRating + briefRating + retakeRating) / 4).toFixed(1)
    : 0;

  const [filters, setFilters] = useState({
    submittedStartDate: null, submittedEndDate: null,
    deliveryStartDate:  null, deliveryEndDate:  null,
    clientId: null, projectId: null, taskId: null,
    taskOwnerId: null, status: 'all', priority: 'all',
  });

  const [clients,    setClients]    = useState([]);
  const [projects,   setProjects]   = useState([]);
  const [tasks,      setTasks]      = useState([]);
  const [taskOwners, setTaskOwners] = useState([]);

  const filtersRef   = useRef(filters);
  const activeTabRef = useRef(activeTab);
  useEffect(() => { filtersRef.current   = filters;   }, [filters]);
  useEffect(() => { activeTabRef.current = activeTab; }, [activeTab]);

  const isNetworkPath = (text) =>
    text && /^\\\\[\w\d.-]+(\\[\w\s.-]+)*/.test(text.trim());

  const isHttpLink = (text) =>
    text && /^https?:\/\/[^\s]+$/i.test(text.trim());

  const handleCopyPath = (path) => {
    navigator.clipboard.writeText(path).then(() => {
      setCopiedPath(path);
      message.success('Path copied to clipboard');
      setTimeout(() => setCopiedPath(null), 2000);
    }).catch(() => message.error('Failed to copy path'));
  };

  const renderTextWithPaths = (text) => {
    if (!text) return null;
    return text.split('\n').map((line, index) => {
      const t = line.trim();
      if (isNetworkPath(t)) {
        const isCopied = copiedPath === t;
        return (
          <div key={index} style={{
            display: 'flex', alignItems: 'center', gap: '8px',
            padding: '8px 12px', background: '#f0f5ff',
            border: '1px solid #d6e4ff', borderRadius: '4px',
            marginBottom: '8px', fontFamily: 'monospace', fontSize: '12px',
          }}>
            <FolderOpenOutlined style={{ color: '#1890ff', fontSize: '16px' }} />
            <span style={{ flex: 1, wordBreak: 'break-all', color: '#1890ff' }}>{t}</span>
            <Button type="text" size="small"
              icon={isCopied ? <CheckOutlined /> : <CopyOutlined />}
              onClick={() => handleCopyPath(t)}
              style={{ color: isCopied ? '#52c41a' : '#1890ff' }}
            />
          </div>
        );
      }
      if (isHttpLink(t)) {
        return (
          <div key={index} style={{
            display: 'flex', alignItems: 'center', gap: '8px',
            padding: '8px 12px', background: '#f6ffed',
            border: '1px solid #b7eb8f', borderRadius: '4px', marginBottom: '8px',
          }}>
            <span style={{ fontSize: '16px' }}>🔗</span>
            <a href={t} target="_blank" rel="noopener noreferrer"
              style={{ flex: 1, wordBreak: 'break-all', color: '#52c41a', fontWeight: 500 }}>
              {t}
            </a>
          </div>
        );
      }
      return (
        <div key={index} style={{ marginBottom: '8px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{line}</div>
      );
    });
  };

  useEffect(() => { loadDropdownData(); fetchInitialData(); }, []);
  useEffect(() => { fetchDataForActiveTab(); fetchFilteredStatistics(); }, [filters, activeTab]);

  // =========================================================
  // WEBSOCKET
  // =========================================================
  useEffect(() => {
    if (!userId) return;
    if (!webSocketService.isConnected()) {
      webSocketService.connect(userId).catch(err => {
        console.warn('ReviewerDashboard: WebSocket connect failed:', err.message);
      });
    }

    const handleNotification = (data) => {
      if (data?.type === 'new_submission') {
        const currentFilters = filtersRef.current;
        const buildParams = (status) => {
          const p = {
            status:    status === 'all' ? currentFilters.status : status,
            startDate: currentFilters.submittedStartDate,
            endDate:   currentFilters.submittedEndDate,
            clientId:  currentFilters.clientId,
            projectId: currentFilters.projectId,
            taskId:    currentFilters.taskId,
            priority:  currentFilters.priority === 'all' ? null : currentFilters.priority,
          };
          Object.keys(p).forEach(k => (p[k] == null || p[k] === '') && delete p[k]);
          return p;
        };

        Promise.all([
          reviewApiV2.getFilteredReviews(userId, buildParams('pending')),
          reviewApiV2.getFilteredReviews(userId, buildParams('approved')),
          reviewApiV2.getFilteredReviews(userId, buildParams('rejected')),
          reviewApiV2.getFilteredReviews(userId, buildParams('retake')),
          reviewApiV2.getFilteredReviews(userId, buildParams('all')),
          reviewApiV2.getFilteredStatistics(userId, (() => {
            const p = buildParams('all'); delete p.status; return p;
          })()),
        ]).then(([pendingRes, approvedRes, rejectedRes, retakeRes, allRes, statsRes]) => {
          if (pendingRes.data.success)  setPendingReviews(pendingRes.data.data   || []);
          if (approvedRes.data.success) setApprovedReviews(approvedRes.data.data || []);
          if (rejectedRes.data.success) setRejectedReviews(rejectedRes.data.data || []);
          if (retakeRes.data.success)   setRetakeReviews(retakeRes.data.data     || []);
          if (allRes.data.success)      setAllReviews(allRes.data.data           || []);
          if (statsRes.data.success)    setStatistics(statsRes.data.data         || {});
        }).catch(err => {
          console.error('ReviewerDashboard: WebSocket refresh failed:', err.message);
        });
      }
    };

    const unsubscribe = webSocketService.on('notification', handleNotification);
    return () => unsubscribe();
  }, [userId]);

  const loadDropdownData = async () => {
    try { const r = await axios.get(`${BASE}/v2/master/getAllClients`);  setClients(r.data?.data   || []); } catch { setClients([]); }
    try { const r = await axios.get(`${BASE}/v2/master/getAllOwners`);   setTaskOwners(r.data?.data || []); } catch { setTaskOwners([]); }
  };

  const loadProjects = async (clientId) => {
    if (!clientId) { setProjects([]); return; }
    try { const r = await axios.get(`${BASE}/v2/master/getProjectsByClientId`, { params: { clientId } }); setProjects(r.data?.data || []); }
    catch { setProjects([]); }
  };

  const loadTasks = async (projectId) => {
    if (!projectId) { setTasks([]); return; }
    try { const r = await axios.get(`${BASE}/v2/task/getTasksByProjectId`, { params: { projectId } }); setTasks(r.data?.data || []); }
    catch { setTasks([]); }
  };

  const fetchInitialData = async () => {
    await Promise.all([
      fetchFilteredReviews('pending'),
      fetchFilteredReviews('approved'),
      fetchFilteredReviews('rejected'),
      fetchFilteredReviews('retake'),
      fetchFilteredReviews('all'),
      fetchFilteredStatistics(),
    ]);
  };

  const fetchDataForActiveTab = () => {
    const map = { '1': 'pending', '2': 'approved', '3': 'rejected', '4': 'retake', '5': 'all' };
    const s   = map[activeTab];
    if (s) fetchFilteredReviews(s);
  };

  const buildFilterParams = (status) => {
    const p = {
      status:    status === 'all' ? filters.status : status,
      startDate: filters.submittedStartDate,
      endDate:   filters.submittedEndDate,
      clientId:  filters.clientId,
      projectId: filters.projectId,
      taskId:    filters.taskId,
      priority:  filters.priority === 'all' ? null : filters.priority,
    };
    Object.keys(p).forEach(k => (p[k] == null || p[k] === '') && delete p[k]);
    return p;
  };

  const fetchFilteredReviews = async (status) => {
    setLoading(true);
    try {
      const response = await reviewApiV2.getFilteredReviews(userId, buildFilterParams(status));
      if (response.data.success) {
        let data = response.data.data || [];
        if (filters.deliveryStartDate || filters.deliveryEndDate) {
          data = data.filter(item => {
            if (!item.owner_delivery_date) return false;
            const d = moment(item.owner_delivery_date).format('YYYY-MM-DD');
            if (filters.deliveryStartDate && d < filters.deliveryStartDate) return false;
            if (filters.deliveryEndDate   && d > filters.deliveryEndDate)   return false;
            return true;
          });
        }
        if (filters.taskOwnerId) {
          data = data.filter(item =>
            item.owner_id === filters.taskOwnerId || item.task_owner_id === filters.taskOwnerId
          );
        }
        switch (status) {
          case 'pending':  setPendingReviews(data);  break;
          case 'approved': setApprovedReviews(data); break;
          case 'rejected': setRejectedReviews(data); break;
          case 'retake':   setRetakeReviews(data);   break;
          case 'all':      setAllReviews(data);      break;
          default: break;
        }
      }
    } catch (error) {
      console.error(`Error fetching ${status} reviews:`, error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFilteredStatistics = async () => {
    setStatsLoading(true);
    try {
      const params = buildFilterParams('all');
      delete params.status;
      const response = await reviewApiV2.getFilteredStatistics(userId, params);
      if (response.data.success) setStatistics(response.data.data || {});
    } catch (error) {
      console.error('Error fetching statistics:', error);
    } finally {
      setStatsLoading(false);
    }
  };

  const handleFilterChange = (key, value) => setFilters(prev => ({ ...prev, [key]: value }));

  const handleClientChange = (value) => {
    setFilters(prev => ({ ...prev, clientId: value || null, projectId: null, taskId: null }));
    setProjects([]); setTasks([]);
    if (value) loadProjects(value);
  };

  const handleProjectChange = (value) => {
    setFilters(prev => ({ ...prev, projectId: value || null, taskId: null }));
    setTasks([]);
    if (value) loadTasks(value);
  };

  const resetFilters = () => {
    setFilters({
      submittedStartDate: null, submittedEndDate: null,
      deliveryStartDate:  null, deliveryEndDate:  null,
      clientId: null, projectId: null, taskId: null,
      taskOwnerId: null, status: 'all', priority: 'all',
    });
    setProjects([]); setTasks([]);
  };

  const resetReviewForm = () => {
    setComments(''); setRemarks('');
    setQualityRating(0); setDeadlineRating(0); setBriefRating(0); setRetakeRating(0);
  };

  const submitReview = async () => {
    if (reviewAction === 'reject' && !comments.trim()) {
      message.warning('Please provide comments for rejection'); return;
    }
    if (reviewAction === 'approve') {
      if (qualityRating  === 0) { message.warning('Please provide Quality Rating');  return; }
      if (deadlineRating === 0) { message.warning('Please provide Deadline Rating'); return; }
      if (briefRating    === 0) { message.warning('Please provide Brief Rating');    return; }
      if (retakeRating   === 0) { message.warning('Please provide Retake Rating');   return; }
      if (!remarks.trim())      { message.warning('Remarks are required');           return; }
    }
    try {
      const avgRating = reviewAction === 'approve'
        ? parseFloat(((qualityRating + deadlineRating + briefRating + retakeRating) / 4).toFixed(1))
        : null;
      const response = await reviewApiV2.submitReviewWithRating({
        submissionId:       selectedTask.submission_id,
        reviewerId:         userId,
        action:             reviewAction === 'approve' ? 'approved' : 'rejected',
        comments:           reviewAction === 'reject' ? comments : remarks,
        qualityRating:      reviewAction === 'approve' ? qualityRating  : null,
        proofreadingRating: null,
        deadlineRating:     reviewAction === 'approve' ? deadlineRating : null,
        briefRating:        reviewAction === 'approve' ? briefRating    : null,
        retakeRating:       reviewAction === 'approve' ? retakeRating   : null,
        internalRating:     avgRating,
      });
      if (response.data.success) {
        message.success(reviewAction === 'approve' ? `Work approved with rating ${avgRating}/5` : 'Work sent back for revision');
        setReviewModal(false); resetReviewForm(); setSelectedTask(null); setReviewAction(null);
        fetchInitialData();
      }
    } catch (error) {
      message.error(error.response?.data?.message || 'Failed to submit review');
    }
  };

  const openReviewFromDetail = (action) => {
    setDetailModal(false); setReviewAction(action); resetReviewForm(); setReviewModal(true);
  };

  const getStatusBadge = (status) => {
    const map = {
      pending:      { color: 'gold',  text: 'Pending' },
      under_review: { color: 'blue',  text: 'Under Review' },
      approved:     { color: 'green', text: 'Approved' },
      rejected:     { color: 'red',   text: 'Rejected' },
    };
    const c = map[status] || { color: 'default', text: status };
    return <Tag color={c.color}>{c.text}</Tag>;
  };

  const getPriorityColor = (p) => ({ High: 'red', Medium: 'orange', Low: 'green' }[p] || 'default');

  const canReview = (record) =>
    !record?.my_review_action &&
    record?.submission_status !== 'approved' &&
    record?.submission_status !== 'rejected';

  const RatingRow = ({ label, value, onChange }) => (
    <div>
      <Text strong>{label}<span style={{ color: 'red', marginLeft: '4px' }}>*</span></Text>
      <div style={{ marginTop: '8px' }}>
        <Rate value={value} onChange={onChange} style={{ fontSize: '24px' }} />
        <Text type="secondary" style={{ marginLeft: '12px' }}>{value}/5</Text>
      </div>
    </div>
  );

  // =========================================================
  // RETAKE REASON CELL
  // =========================================================
  const RetakeReasonCell = ({ record }) => {
    const reason = record.my_review_comments || record.comments;
    if (!reason) return <Text style={{ color: '#bbb', fontSize: 12 }}>No reason recorded</Text>;
    return (
      <Tooltip title={reason} placement="topLeft">
        <div style={{
          maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis',
          whiteSpace: 'nowrap', fontSize: 12, color: '#fa541c',
        }}>
          {reason}
        </div>
      </Tooltip>
    );
  };

  // =========================================================
  // BASE COLUMNS — shared across all tabs
  // Brief column is inserted after Project, before Task
  // =========================================================
  const baseColumns = [
    {
      title: 'Client', dataIndex: 'client_name', key: 'client_name', width: '8%',
      render: (t) => <Tag color="blue">{t}</Tag>,
    },
    {
      title: 'Project', dataIndex: 'project_name', key: 'project_name', width: '10%',
      render: (t) => <Tag color="purple">{t}</Tag>,
    },
    // -------------------------------------------------------
    // BRIEF COLUMN — NEW
    // Renders a clickable button linking to the project brief.
    // Falls back to "No brief" text if brief_link is null/empty.
    // Handles both http URLs and network paths (\\server\path).
    // -------------------------------------------------------
    {
      title: 'Brief', dataIndex: 'brief_link', key: 'brief_link', width: '7%', align: 'center',
      render: (link) => <BriefLinkCell link={link} />,
    },
    {
      title: 'Task', key: 'task', width: '15%',
      render: (_, r) => {
        const v  = String(r.version          || 0).padStart(2, '0');
        const iv = String(r.internal_version || 0).padStart(2, '0');
        return (
          <Text strong>
            {r.task_name}
            <span style={{ color: '#1890ff', marginLeft: '8px', fontSize: '12px', fontWeight: 'normal' }}>{v}-{iv}</span>
          </Text>
        );
      },
    },
    {
      title: 'Task Owner', key: 'owner', width: '9%',
      render: (_, r) => <Text style={{ fontSize: '13px' }}>{r.owner_name}</Text>,
    },
    {
      title: 'Submitted', dataIndex: 'submitted_at', key: 'submitted_at', width: '10%',
      render: (v) => {
        if (!v) return '-';
        const d = new Date(v);
        return (
          <div>
            <div style={{ fontSize: '13px', fontWeight: 500 }}>
              {d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
            </div>
            <div style={{ fontSize: '12px', color: '#8c8c8c' }}>
              {d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })}
            </div>
          </div>
        );
      },
    },
    {
      title: 'Delivery', dataIndex: 'owner_delivery_date', key: 'delivery_date', width: '9%',
      render: (d) => d ? moment(d).format('DD MMM YYYY') : 'N/A',
    },
    {
      title: 'Priority', dataIndex: 'priority', key: 'priority', width: '7%',
      render: (p) => <Tag color={getPriorityColor(p)}>{p}</Tag>,
    },
    {
      title: 'Status', key: 'status', width: '9%',
      render: (_, r) => (
        <Space direction="vertical" size={4}>
          {getStatusBadge(r.submission_status)}
          {r.internal_rating > 0 && r.my_review_action === 'approved' && (
            <Rate disabled allowHalf value={r.internal_rating} style={{ fontSize: '14px', color: '#FFD700' }} />
          )}
        </Space>
      ),
    },
    {
      title: 'Actions', key: 'actions', width: '10%', align: 'center',
      render: (_, r) => (
        <Space size={6}>
          <Button size="small" icon={<EyeOutlined />} onClick={() => { setSelectedTask(r); setDetailModal(true); }}>
            View
          </Button>
          <Tooltip title={`Chat — ${r.project_name}`} placement="left">
            <Button size="small" icon={<MessageOutlined />} onClick={() => openChatDrawer(r)}
              style={{ color: '#52c41a', borderColor: '#52c41a' }} />
          </Tooltip>
        </Space>
      ),
    },
  ];

  // Retake tab adds Retake Reason before Actions
  const retakeColumns = [
    ...baseColumns.slice(0, baseColumns.length - 1),
    {
      title: 'Retake Reason', key: 'retake_reason', width: '14%',
      render: (_, r) => <RetakeReasonCell record={r} />,
    },
    baseColumns[baseColumns.length - 1],
  ];

  const tableProps = (data, cols = baseColumns) => ({
    columns:    cols,
    dataSource: data,
    loading,
    rowKey:     (r) => r.submission_id,
    pagination: { pageSize: 10, showTotal: (t) => `Total ${t} items` },
    scroll:     { x: 1300 },
  });

  return (
    <div style={{ padding: '24px', backgroundColor: '#f0f2f5', minHeight: '100vh' }}>

      {/* Filters */}
      <Card
        title={<Space><FilterOutlined /><span>Filters</span></Space>}
        style={{ marginBottom: '24px' }}
        extra={<Button onClick={resetFilters} size="small">Reset Filters</Button>}
      >
        <Row gutter={[16, 16]}>
          <Col span={3}>
            <Text type="secondary">Submitted Date Range</Text>
            <RangePicker style={{ width: '100%', marginTop: '4px' }}
              value={filters.submittedStartDate && filters.submittedEndDate ? [moment(filters.submittedStartDate), moment(filters.submittedEndDate)] : null}
              onChange={(dates) => setFilters(prev => ({ ...prev, submittedStartDate: dates?.[0]?.format('YYYY-MM-DD') || null, submittedEndDate: dates?.[1]?.format('YYYY-MM-DD') || null }))}
              format="DD MMM YYYY" placeholder={['Start Date', 'End Date']} />
          </Col>
          <Col span={3}>
            <Text type="secondary">Delivery Date Range</Text>
            <RangePicker style={{ width: '100%', marginTop: '4px' }}
              value={filters.deliveryStartDate && filters.deliveryEndDate ? [moment(filters.deliveryStartDate), moment(filters.deliveryEndDate)] : null}
              onChange={(dates) => setFilters(prev => ({ ...prev, deliveryStartDate: dates?.[0]?.format('YYYY-MM-DD') || null, deliveryEndDate: dates?.[1]?.format('YYYY-MM-DD') || null }))}
              format="DD MMM YYYY" placeholder={['Start Date', 'End Date']} />
          </Col>
          <Col span={3}>
            <Text type="secondary">Client</Text>
            <Select style={{ width: '100%', marginTop: '4px' }} placeholder="Select Client" value={filters.clientId} onChange={handleClientChange} allowClear showSearch filterOption={(i, o) => o.children.toLowerCase().includes(i.toLowerCase())}>
              {clients.map(c => <Option key={c.clientId} value={c.clientId}>{c.clientName}</Option>)}
            </Select>
          </Col>
          <Col span={3}>
            <Text type="secondary">Project</Text>
            <Select style={{ width: '100%', marginTop: '4px' }} placeholder={filters.clientId ? 'Select Project' : 'Select client first'} value={filters.projectId} onChange={handleProjectChange} allowClear showSearch disabled={!filters.clientId} filterOption={(i, o) => o.children.toLowerCase().includes(i.toLowerCase())}>
              {projects.map(p => <Option key={p.projectId} value={p.projectId}>{p.projectName}</Option>)}
            </Select>
          </Col>
          <Col span={3}>
            <Text type="secondary">Task</Text>
            <Select style={{ width: '100%', marginTop: '4px' }} placeholder={filters.projectId ? 'Select Task' : 'Select project first'} value={filters.taskId} onChange={(v) => handleFilterChange('taskId', v || null)} allowClear showSearch disabled={!filters.projectId} filterOption={(i, o) => o.children.toLowerCase().includes(i.toLowerCase())}>
              {tasks.map(t => <Option key={t.taskId} value={t.taskId}>{t.taskName}</Option>)}
            </Select>
          </Col>
          <Col span={3}>
            <Text type="secondary">Task Owner</Text>
            <Select style={{ width: '100%', marginTop: '4px' }} placeholder="Select Owner" value={filters.taskOwnerId} onChange={(v) => handleFilterChange('taskOwnerId', v || null)} allowClear showSearch filterOption={(i, o) => o.children.toLowerCase().includes(i.toLowerCase())}>
              {taskOwners.map(o => <Option key={o.employeeId} value={o.employeeId}>{o.employeeName}</Option>)}
            </Select>
          </Col>
          <Col span={3}>
            <Text type="secondary">Priority</Text>
            <Select style={{ width: '100%', marginTop: '4px' }} value={filters.priority} onChange={(v) => handleFilterChange('priority', v)}>
              <Option value="all">All Priorities</Option>
              <Option value="High">High</Option>
              <Option value="Medium">Medium</Option>
              <Option value="Low">Low</Option>
            </Select>
          </Col>
          {activeTab === '5' && (
            <Col span={4}>
              <Text type="secondary">Status</Text>
              <Select style={{ width: '100%', marginTop: '4px' }} value={filters.status} onChange={(v) => handleFilterChange('status', v)}>
                <Option value="all">All Status</Option>
                <Option value="pending">Pending</Option>
                <Option value="under_review">Under Review</Option>
                <Option value="approved">Approved</Option>
                <Option value="rejected">Rejected</Option>
              </Select>
            </Col>
          )}
        </Row>
      </Card>

      {/* Statistics */}
      {/* <Row gutter={16} style={{ marginBottom: '24px' }}>
        {[
          { label: 'Pending Reviews', key: 'pending_count',  color: '#faad14' },
          { label: 'Approved',        key: 'approved_count', color: '#52c41a' },
          { label: 'Rejected',        key: 'rejected_count', color: '#ff4d4f' },
          { label: 'Awaiting Retake', key: 'retake_count',   color: '#fa541c' },
          { label: 'Total Reviews',   key: 'total_reviews',  color: '#1890ff' },
        ].map(({ label, key, color }) => (
          <Col span={key === 'total_reviews' ? 4 : 5} key={key}>
            <Card loading={statsLoading}>
              <div style={{ textAlign: 'center' }}>
                <Title level={2} style={{ margin: 0, color }}>{statistics[key] || 0}</Title>
                <Text type="secondary">{label}</Text>
              </div>
            </Card>
          </Col>
        ))}
      </Row> */}

      {/* Review Tables */}
      <Card>
        <Tabs activeKey={activeTab} onChange={setActiveTab} items={[
          { key: '1', label: `Pending (${pendingReviews.length})`,   children: <Table {...tableProps(pendingReviews)} /> },
          { key: '2', label: `Approved (${approvedReviews.length})`, children: <Table {...tableProps(approvedReviews)} /> },
          // { key: '3', label: `Rejected (${rejectedReviews.length})`, children: <Table {...tableProps(rejectedReviews)} /> },
          {
            key: '4',
            label: (
              <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                {/* <ReloadOutlined style={{ color: '#fa541c' }} /> */}
                Retake ({retakeReviews.length})
              </span>
            ),
            children: (
              <div>
                <Alert
                  type="warning" showIcon
                  message="These tasks were rejected and are waiting for the owner to fix and resubmit. They will disappear automatically once the owner resubmits."
                  style={{ marginBottom: 16 }}
                />
                <Table {...tableProps(retakeReviews, retakeColumns)} />
              </div>
            ),
          },
          { key: '5', label: `All (${allReviews.length})`, children: <Table {...tableProps(allReviews)} /> },
        ]} />
      </Card>

      {/* Review Modal */}
      <Modal
        title={reviewAction === 'approve' ? 'Approve Work' : 'Request Retake'}
        open={reviewModal}
        onOk={submitReview}
        onCancel={() => { setReviewModal(false); resetReviewForm(); }}
        width={600}
        okText={reviewAction === 'approve' ? 'Approve' : 'Send for Retake'}
        okButtonProps={{
          style: reviewAction === 'approve'
            ? { backgroundColor: '#52c41a', borderColor: '#52c41a' }
            : { backgroundColor: '#ff4d4f', borderColor: '#ff4d4f' },
        }}
      >
        {selectedTask && (
          <Space direction="vertical" style={{ width: '100%' }} size="large">
            <Descriptions bordered size="small" column={1}>
              <Descriptions.Item label="Task">{selectedTask.task_name}</Descriptions.Item>
              <Descriptions.Item label="Version">
                {String(selectedTask.version || 0).padStart(2, '0')}-{String(selectedTask.internal_version || 0).padStart(2, '0')}
              </Descriptions.Item>
              <Descriptions.Item label="Owner">{selectedTask.owner_name}</Descriptions.Item>
              <Descriptions.Item label="Submitted">{moment(selectedTask.submitted_at).format('DD MMM YYYY, hh:mm A')}</Descriptions.Item>
            </Descriptions>

            {reviewAction === 'approve' && (
              <>
                <RatingRow label="Quality Rating"  value={qualityRating}  onChange={setQualityRating} />
                <RatingRow label="Deadline Rating" value={deadlineRating} onChange={setDeadlineRating} />
                <RatingRow label="Brief Rating"    value={briefRating}    onChange={setBriefRating} />
                <RatingRow label="Retake Rating"   value={retakeRating}   onChange={setRetakeRating} />
                {allRatingsFilled && (
                  <div style={{ backgroundColor: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: '4px', padding: '12px' }}>
                    <Text strong>Internal Rating (Auto-calculated)</Text>
                    <div style={{ marginTop: '8px' }}>
                      <Rate disabled allowHalf value={parseFloat(internalRating)} style={{ fontSize: '24px', color: '#FFD700' }} />
                      <Text type="secondary" style={{ marginLeft: '12px', fontSize: '16px' }}>{internalRating}/5</Text>
                    </div>
                  </div>
                )}
                <div>
                  <Text strong>Remarks <span style={{ color: 'red' }}>*</span></Text>
                  <TextArea rows={3} value={remarks} onChange={(e) => setRemarks(e.target.value)} placeholder="Remarks are required" style={{ marginTop: '8px' }} />
                </div>
              </>
            )}

            {reviewAction === 'reject' && (
              <div>
                <Text strong>Comments <span style={{ color: 'red' }}>*</span></Text>
                <TextArea rows={4} value={comments} onChange={(e) => setComments(e.target.value)} placeholder="Explain what needs to be fixed (required)" style={{ marginTop: '8px' }} />
              </div>
            )}
          </Space>
        )}
      </Modal>

      {/* Detail Modal */}
      <Modal
        title="Task Details"
        open={detailModal}
        onCancel={() => setDetailModal(false)}
        width={800}
        styles={{ body: { maxHeight: '80vh', overflowY: 'auto' } }}
        footer={[
          <Button key="close" onClick={() => setDetailModal(false)}>Close</Button>,
          ...(canReview(selectedTask) ? [
            <Button key="retake" danger icon={<CloseCircleOutlined />} onClick={() => openReviewFromDetail('reject')}>Retake</Button>,
            <Button key="approve" icon={<CheckCircleOutlined />} style={{ backgroundColor: '#52c41a', borderColor: '#52c41a', color: '#fff' }} onClick={() => openReviewFromDetail('approve')}>Approve</Button>,
          ] : []),
        ]}
      >
        {selectedTask && (
          <>
            <Descriptions bordered column={2}>
              <Descriptions.Item label="Client"  span={2}><Tag color="blue">{selectedTask.client_name}</Tag></Descriptions.Item>
              <Descriptions.Item label="Project" span={2}><Tag color="purple">{selectedTask.project_name}</Tag></Descriptions.Item>
              <Descriptions.Item label="Task"    span={2}>{selectedTask.task_name}</Descriptions.Item>
              <Descriptions.Item label="Version">
                {String(selectedTask.version || 0).padStart(2, '0')}-{String(selectedTask.internal_version || 0).padStart(2, '0')}
              </Descriptions.Item>
              <Descriptions.Item label="Priority">
                <Tag color={getPriorityColor(selectedTask.priority)}>{selectedTask.priority}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Task Owner"    span={2}>{selectedTask.owner_name}</Descriptions.Item>
              <Descriptions.Item label="Submitted At"  span={2}>{moment(selectedTask.submitted_at).format('DD MMM YYYY, hh:mm A')}</Descriptions.Item>
              <Descriptions.Item label="Delivery Date" span={2}>
                {selectedTask.owner_delivery_date ? moment(selectedTask.owner_delivery_date).format('DD MMM YYYY') : 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Project Brief" span={2}>
                <BriefLinkCell link={selectedTask.brief_link} />
              </Descriptions.Item>
              <Descriptions.Item label="Status" span={2}>{getStatusBadge(selectedTask.submission_status)}</Descriptions.Item>
            </Descriptions>

            <Divider style={{ margin: '16px 0' }} />

            <SubmissionHistory
              taskId={selectedTask.task_id}
              version={selectedTask.version}
              ownerId={selectedTask.owner_id}
              currentUserId={userId}
              onReviewEdited={fetchInitialData}
            />
          </>
        )}
      </Modal>

      {/* Chat Drawer */}
      <Drawer
        open={chatDrawerOpen}
        onClose={closeChatDrawer}
        width={480}
        styles={{
          body:   { padding: 0, height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' },
          header: { borderBottom: '1px solid #e8e8e8', padding: '12px 16px' },
        }}
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <MessageOutlined style={{ color: '#52c41a', fontSize: 16 }} />
            <span style={{ fontWeight: 600, fontSize: 14, color: '#262626' }}>
              {chatProjectData?.projectName || 'Project Chat'}
            </span>
            <Tag color="green" style={{ margin: 0, fontSize: 11, lineHeight: '18px' }}>Project</Tag>
          </div>
        }
        destroyOnClose
      >
        {chatProjectData && (
          <ProjectChat
            projectData={chatProjectData}
            visible={chatDrawerOpen}
            onClose={closeChatDrawer}
            currentUserId={userId}
          />
        )}
      </Drawer>

    </div>
  );
};

export default ReviewerDashboard;